import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Game } from '../data/games';

type GameHeroProps = {
  game: Game;
};

function shouldLoadHeroVideo(): boolean {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return false;
  }

  const connection = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!connection) {
    return true;
  }

  if (connection.saveData) {
    return false;
  }

  const slowTypes = ['slow-2g', '2g', '3g'];
  if (connection.effectiveType && slowTypes.includes(connection.effectiveType)) {
    return false;
  }

  return true;
}

function GameHero({ game }: GameHeroProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loadVideo, setLoadVideo] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const videoSrc = game.heroVideo ? encodeURI(game.heroVideo) : '';
  const showVideo = Boolean(game.heroVideo) && loadVideo && !videoFailed;

  useEffect(() => {
    setLoadVideo(false);
    setVideoFailed(false);
    setVideoReady(false);

    if (!game.heroVideo || !shouldLoadHeroVideo()) {
      return undefined;
    }

    const startLoading = () => setLoadVideo(true);
    const idleCallback = window.requestIdleCallback?.(startLoading, { timeout: 1500 });
    const fallbackTimer = window.setTimeout(startLoading, 800);

    return () => {
      if (idleCallback !== undefined) {
        window.cancelIdleCallback(idleCallback);
      }
      window.clearTimeout(fallbackTimer);
    };
  }, [game.slug, game.heroVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) {
      return undefined;
    }

    const handleCanPlay = () => {
      setVideoReady(true);
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch((error: Error) => {
          if (error.name !== 'AbortError') {
            setVideoFailed(true);
          }
        });
      }
    };

    video.addEventListener('canplay', handleCanPlay);

    if (video.readyState >= 3) {
      handleCanPlay();
    }

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [showVideo, game.slug]);

  return (
    <section className="kg-game-hero">
      <div className="kg-game-hero-media">
        <img
          src={game.featuredImage}
          alt={game.title}
          className="kg-game-hero-banner"
          fetchPriority="high"
          decoding="async"
          onError={(event) => {
            event.currentTarget.src = game.image;
          }}
        />
        {showVideo ? (
          <video
            ref={videoRef}
            id="game-trailer"
            key={game.slug}
            className={`kg-game-hero-video${videoReady ? ' is-ready' : ''}`}
            src={videoSrc}
            poster={game.featuredImage}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onError={() => setVideoFailed(true)}
          />
        ) : null}
        <div className="kg-game-hero-overlay" />
        <div className="kg-game-hero-content">
          <Link to="/" className="kg-game-back">
            ← All Games
          </Link>
          <p className="kg-game-hero-badge">{game.badge}</p>
          <h1 className="kg-game-hero-title">{game.title}</h1>
        </div>
      </div>
    </section>
  );
}

export default GameHero;
